import {
    FormComponentFieldAttributes,
    FormComponentFieldProps,
} from "components/Form/components/FormComponentProps";
import { InputComponentProps } from "components/Form/components/InputComponentProps";
import { Icon } from "components/Icon/Icon";
import { Toggle } from "rsuite";
import { toDisplayedSwitchValue, toStoredSwitchValue } from "./invertedSwitchValue";
import styles from "./Switch.module.scss";

export type SwitchProps = {
    identifier: string;
    inputProps: InputComponentProps<boolean>;
    attributes?: FormComponentFieldAttributes;
    fieldProps: FormComponentFieldProps;
};

interface SimpleToggleProps {
    identifier: string;
    value?: boolean;
    isReadOnly?: boolean;
    size?: "lg" | "md" | "sm" | "xs";
    additionalAttributes?: Record<string, any>;
}
export const SimpleToggle = ({
    identifier,
    value,
    isReadOnly,
    size = "lg",
    additionalAttributes = {},
}: SimpleToggleProps) => {
    const isDisabled = isReadOnly || additionalAttributes.disabled;
    return (
        <Toggle
            {...additionalAttributes}
            id={identifier}
            aria-label={identifier}
            size={size}
            defaultChecked={value === true}
            checked={value === true}
            disabled={isDisabled}
            className={`${additionalAttributes.className ?? ""} ${
                isDisabled ? styles.toggleDisabled : ""
            }`}
        />
    );
};

export const Switch = ({ identifier, inputProps, attributes, fieldProps }: SwitchProps) => {
    const isIconOnly = fieldProps?.renderIconOnly;
    const inverted = fieldProps?.inverted;
    const displayedValue = toDisplayedSwitchValue(inputProps.value, inverted);
    const toggleInputProps = inverted
        ? {
              ...inputProps,
              onChange: (checked: boolean) => inputProps.onChange?.(toStoredSwitchValue(checked, true)),
          }
        : inputProps;

    const toggleInner = (
        <SimpleToggle
            additionalAttributes={{ ...attributes, ...toggleInputProps }}
            identifier={identifier}
            size={attributes?.size}
            value={displayedValue}
            isReadOnly={inputProps.isReadOnly}
        />
    );

    return (
        <div className={isIconOnly ? styles.toggleIconContainer : styles.switch}>
            {isIconOnly ? (
                <>
                    <div className={`${styles.innerComponent} ${styles.toggleInner}`}>
                        {toggleInner}
                    </div>
                    <div
                        className={`${styles.innerComponent} ${styles.toggleOption} ${
                            displayedValue ? styles.selectedOption : ""
                        }`}
                    >
                        <Icon icon={fieldProps.iconOnlyComponent} />
                    </div>
                </>
            ) : (
                <>
                    {toggleInner}
                    <span
                        className={`switch__label${
                            displayedValue ? " switch__label--active" : ""
                        }`}
                    >
                        Yes
                    </span>
                </>
            )}
        </div>
    );
};
